import React, {useState, useEffect} from 'react';
import { AreaChart, Area, Line, XAxis, YAxis, ReferenceLine, Cell, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Label } from 'recharts';
import { convertSingleValue, convertCurrency, convertCurrencyMulti } from '../utils/commonUtils';

const ManualMarketLineGraph = ({ data, height = 500, width, convertValue=false, showLabel='auto', colorType='default', showXaxisName=true, figWidth=null }) => {
    const [isSmallArea, setIsSmallArea] = useState(false);
    const [activeIndex, setActiveIndex] = useState(null);

    useEffect(() => {
        if (data.length == 0) return;
        if (width / data[0]?.x.length / data.length < 80 ) {
            setIsSmallArea(true);
        } else {
            setIsSmallArea(false);
        }
    }, [width, data])

    const handleMouseEnter = (_, index) => {
        setActiveIndex(index)
    }

    const handleMouseLeave = () => {
        setActiveIndex(null)
    }

    const convertToChartData = (data) => {
        let modifiedData;
        if (convertValue) { 
            const dataForConvert = data.map((series) => {
                return series.y;
            })
            const convertedData = convertCurrencyMulti(dataForConvert)
            modifiedData = data.map((series, index) => {
                return {
                    x: series.x,
                    x_unit: series.x_unit,
                    y: series.y,
                    y_unit: series.y_unit,
                    name: series.name,
                }
            })
        } else {
            modifiedData = data;
        }
        
        const chartData = modifiedData[0].x.map((xValue, index) => {
            let chartDataPoint = { x: xValue };
            modifiedData.forEach((series, seriesIndex) => {
                chartDataPoint[`y${seriesIndex}`] = series.y[index];
                chartDataPoint[`y${seriesIndex}_name`] = series.name;
                chartDataPoint[`y_unit`] = series.y_unit;
                chartDataPoint[`x_unit`] = series.x_unit;
            });
            return chartDataPoint;
        });
    
        // chartDataとmodifiedDataの両方を返す
        return { chartData, modifiedData };
    };

    const  { chartData, modifiedData } = convertToChartData(data);
        
    let stopColor = []

    if (colorType === 'default') {
        stopColor = [
            {start: "rgba(45, 186, 177, .5)", end: "rgba(45, 186, 77, .5)", toolTip: "rgba(45, 186, 77, 1)"},
            { start: 'rgba(145, 186, 250, 1)', end: 'rgba(80, 140, 200, 1)' , toolTip: "rgba(80, 140, 200, 1)"},
            { start: 'rgba(225, 97, 50, 1)', end: 'rgba(255, 120, 90, 1)' , toolTip: "rgba(255, 120, 90, 1)"},
            { start: 'rgba(147, 112, 219, 1)', end: 'rgba(75, 0, 130, 1)' , toolTip: "rgba(75, 0, 130, 1))"},
            { start: 'rgba(255, 235, 59, 1)', end: 'rgba(251, 192, 45, 1)' , toolTip: "rgba(251, 192, 45, 1)"},
        ];
    } else if (colorType === 'popular') {
        stopColor = [
            { start: 'rgba(145, 186, 250, 1)', end: 'rgba(80, 140, 200, 1)' , toolTip: "rgba(80, 140, 200, 1)"},
            { start: 'rgba(225, 97, 50, 1)', end: 'rgba(255, 120, 90, 1)', toolTip: "rgba(255, 120, 90, 1)" },
            {start: "rgba(45, 186, 177, .5)", end: "rgba(45, 186, 77, .5)", toolTip: "rgba(45, 186, 77, 1)"},
            { start: 'rgba(147, 112, 219, 1)', end: 'rgba(75, 0, 130, 1)' , toolTip: "rgba(75, 0, 130, 1))"},
            { start: 'rgba(255, 235, 59, 1)', end: 'rgba(251, 192, 45, 1)' , toolTip: "rgba(251, 192, 45, 1)"},
        ];
    }
    

    const CustomLabel = ({ x, y, width, value, unit }) => {
        if (value === null || value === undefined) return null;
        const offset = value >= 0 ? -10 : 20;
        return (
            <text
                x={x}
                y={y + offset}
                fill="black"
                textAnchor="middle"
                fontSize={12}
            >
              {convertSingleValue(value)}{unit}
            </text>
        );
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip px-3 py-2 border border-gray-300 bg-gray-600 rounded shadow text-white">
                    <div>{`${payload[0].payload.x}${payload[0].payload.x_unit}`}</div>
                    {payload.map((item, index) => {
                        const seriesIndex = parseInt(item.dataKey.replace('y', ''), 10);  // yのインデックスを取得
                        const labelColor = stopColor[seriesIndex]?.start || 'rgba(100, 100, 100, 0.7)';
                        return (
                            <div key={index} className="flex items-center space-x-2">
                                <svg width="12" height="12" className="flex-shrink-0">
                                    <circle
                                        cx="6" // Circle center X
                                        cy="6" // Circle center Y
                                        r="6" // Circle radius
                                        fill={labelColor}
                                    />
                                </svg>
                                <div className="flex items-center">
                                    {`${item.name} : ${convertSingleValue(item.value, "", 2)}${item.payload.y_unit}`}
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        }
        return null;
    };

    const renderCustomLegend = () => (
        <div className="flex flex-wrap gap-4 pb-[20px]">
            {modifiedData.map((series, index) => (
                <div 
                    key={index} 
                    className="flex items-center" 
                    style={{ color: stopColor[index % stopColor.length].start }}
                >
                    <div className="mr-2 flex items-center">
                        <svg width="10" height="10">
                            <line 
                                x1="0" y1="5" x2="10" y2="5" 
                                stroke={stopColor[index % stopColor.length].start} 
                                strokeWidth="2" 
                            />
                        </svg>
                    </div>
                    <div className="text-gray-600">{series.name}</div>
                </div>
            ))}
        </div>
    );
    
    const japaneseTickFormatter = (value, unit) => {
        if (Math.abs(value) >= 1e12) {
            return (value / 1e12).toFixed(1) + "兆" + unit; // 1兆 = 10^12
        } else if (Math.abs(value) >= 1e8) {
            return (value / 1e8).toFixed(1) + "億" + unit; // 1億 = 10^8
        }
        return value.toString() + unit; // 1億未満はそのまま表示
    };
    
    
    const range = (y) => {
        const yMax = Math.max(...y);
        const yMin = Math.min(...y);
        const range = yMax - yMin
        if (yMin > 0) {
            const rangeMax = yMax * 1.2
            const rangeMin = 0;
            return [rangeMin, rangeMax]
        } else if (yMax < 0) {
            const rangeMax = 0
            const rangeMin = yMin * 1.2;
            return [rangeMin, rangeMax]
        } else {
            const rangeMax = yMax + range * 0.1
            const rangeMin = yMin - range * 0.1
            return [rangeMin, rangeMax]
        }
    }

    return (
        <ResponsiveContainer width={figWidth ? figWidth : "100%"} height={height}>
            <AreaChart
                data={chartData}
                margin={{ top: ((showLabel === 'auto' && !isSmallArea) || showLabel === 'visible') ? 0: 40, right: 30, left: 30, bottom: 40 }}
            >
                <defs>
                    {stopColor.map((color, index) => (
                        <linearGradient id={`gradient${index}`} key={index} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={color.start} />
                            <stop offset="95%" stopColor={color.end} />
                        </linearGradient>
                    ))}
                </defs>
                <ReferenceLine y={0} stroke="lightgray" />
                {((showLabel === 'auto' && isSmallArea) || showLabel==='hidden') && <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />}
                <YAxis
                    orientation="right"
                    tickFormatter={(value) => japaneseTickFormatter(value, '')}
                    tickCount={4}
                    domain={[dataMin => dataMin * 0.8, 'auto']} 
                >
                    <Label
                    position="top"
                    offset={20}
                    style={{ textAnchor: 'middle', fill: 'grey' }}
                    />
                </YAxis>
                
                <XAxis
                    dataKey="x"
                    tick={{ fontSize: 12, fill: 'grey' }}
                >
                    
                    <Label
                        value={showXaxisName ? data[0].x_unit : ""}
                        position="bottom"
                        offset={10}
                        style={{ textAnchor: 'middle', fill: 'grey' }}
                    />
                </XAxis>
                <Tooltip
                    content={<CustomTooltip />}
                    cursor={false} 
                />
                <Legend content={renderCustomLegend} verticalAlign="top" align="left" />

                {modifiedData.map((series, seriesIndex) => (
                    <Area
                        key={seriesIndex}
                        dataKey={`y${seriesIndex}`}
                        fill={`url(#gradient${seriesIndex % stopColor.length})`}
                        type='linear'
                        stroke={stopColor[seriesIndex]['end']}
                        strokeWidth={2}
                        name={series['name']}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    >
                        {series.x.map((_, index) => (
                            <Cell
                            key={`cell-${index}`}
                            opacity={activeIndex === null || index === activeIndex ? 1 : 0.3}
                            />
                            
                        ))}
                    </Area>
                ))}
            </AreaChart>
        </ResponsiveContainer>
    );
};

export default ManualMarketLineGraph;
